import React, { useEffect, useState } from 'react';
import { Menu, Plus, LogOut, ChevronDown, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import NotificationBell from '@/components/notifications/NotificationBell';
import { initials } from '@/lib/format';
import { toast } from 'sonner';

export default function TopHeader({ onMenu, onQuickAdd }) {
    const { profile, signOut, user } = useAuth();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [changePwOpen, setChangePwOpen] = useState(false);

    useEffect(() => {
        const close = () => setMenuOpen(false);
        window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, []);

    const role = profile?.role || 'agent';

    return (
        <>
            <header className="h-14 bg-white border-b border-gray-200 flex items-center px-4 lg:px-6 sticky top-0 z-30" data-testid="top-header">
                <button className="lg:hidden mr-3 text-aks-navy" onClick={onMenu} aria-label="Open menu">
                    <Menu className="h-5 w-5" />
                </button>
                <div className="flex-1" />
                <button
                    onClick={onQuickAdd}
                    className="hidden md:inline-flex items-center gap-2 h-9 px-3.5 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px] hover:brightness-105 active:brightness-95 aks-focus-ring transition mr-2"
                    data-testid="top-quick-add"
                >
                    <Plus className="h-4 w-4" /> Quick add <span className="text-[11px] font-medium opacity-70 ml-1 px-1 border border-aks-navy/20 rounded">N</span>
                </button>
                <NotificationBell />
                <div className="relative ml-2" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setMenuOpen((s) => !s)} className="flex items-center gap-2 h-9 px-2 rounded-md hover:bg-gray-100 aks-focus-ring" data-testid="user-menu-trigger">
                        <div className="h-7 w-7 rounded-full bg-aks-navy text-white flex items-center justify-center text-[11px] font-semibold">
                            {initials(profile?.full_name || user?.email)}
                        </div>
                        <div className="hidden md:block text-left">
                            <div className="text-[13px] font-semibold text-aks-text leading-none">{profile?.full_name || user?.email}</div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">{role}</div>
                        </div>
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 top-11 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50" data-testid="user-menu">
                            <div className="px-3 py-2 border-b border-gray-100">
                                <div className="text-[12px] text-gray-500">Signed in as</div>
                                <div className="text-[13px] font-medium text-aks-text truncate">{user?.email}</div>
                            </div>
                            {role === 'admin' && (
                                <button className="w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50" onClick={() => navigate('/settings/profile')}>
                                    Profile & settings
                                </button>
                            )}
                            <button
                                className="w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 flex items-center gap-2"
                                onClick={() => { setMenuOpen(false); setChangePwOpen(true); }}
                                data-testid="change-password-btn"
                            >
                                <KeyRound className="h-4 w-4 text-gray-500" /> Change password
                            </button>
                            <button className="w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 text-aks-danger flex items-center gap-2" onClick={signOut} data-testid="sign-out-btn">
                                <LogOut className="h-4 w-4" /> Sign out
                            </button>
                        </div>
                    )}
                </div>
            </header>
            {changePwOpen && <ChangePasswordModal user={user} onClose={() => setChangePwOpen(false)} />}
        </>
    );
}

function ChangePasswordModal({ user, onClose }) {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next !== confirm) { toast.error('Passwords do not match'); return; }
        if (next.length < 6) { toast.error('New password must be at least 6 characters'); return; }
        setSaving(true);
        try {
            const { error: authErr } = await supabase.auth.signInWithPassword({ email: user.email, password: current });
            if (authErr) throw new Error('Current password is incorrect');
            const { error } = await supabase.auth.updateUser({ password: next });
            if (error) throw error;
            toast.success('Password updated successfully');
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
                <h3 className="text-lg font-bold text-aks-text mb-4">Change password</h3>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="text-[12px] font-medium text-aks-text">Current password</label>
                        <input required type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px] outline-none focus:border-aks-gold aks-focus-ring" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium text-aks-text">New password</label>
                        <input required type="password" value={next} onChange={(e) => setNext(e.target.value)} minLength={6} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px] outline-none focus:border-aks-gold aks-focus-ring" placeholder="Min. 6 characters" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium text-aks-text">Confirm new password</label>
                        <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px] outline-none focus:border-aks-gold aks-focus-ring" />
                    </div>
                    <div className="flex gap-2 pt-1">
                        <button type="button" onClick={onClose} className="flex-1 h-10 rounded-md border border-gray-300 text-[13px] font-medium hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={saving} className="flex-1 h-10 rounded-md bg-aks-navy text-white text-[13px] font-semibold disabled:opacity-60">
                            {saving ? 'Saving…' : 'Update password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
